import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Ban, Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const today = () => new Date().toLocaleDateString('sv-SE');
const showDate = (d, el) => new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(el ? 'el-GR' : 'en-GB');

export const TerminatedOn = ({ vehicle, el }) => (
    <span className="text-xs text-slate-500" data-testid={`leasing-vehicle-terminated-at-${vehicle.id}`}>{vehicle.terminated_at ? new Date(vehicle.terminated_at).toLocaleDateString(el ? 'el-GR' : 'en-GB') : ''}</span>
);

export const ScheduledTermination = ({ vehicle, el, onChanged }) => {
    const cancel = async () => {
        try {
            await api.delete(`/leasing/vehicles/${vehicle.id}/terminate`);
            toast.success(el ? 'Ο τερματισμός ακυρώθηκε' : 'Termination cancelled');
            onChanged();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        }
    };
    return (
        <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded" data-testid={`leasing-vehicle-termination-${vehicle.id}`}>
            {el ? 'Τερματισμός' : 'Ends'}: {showDate(vehicle.termination_date, el)}
            <button onClick={cancel} className="ml-1 hover:text-red-900" title={el ? 'Ακύρωση τερματισμού' : 'Cancel termination'} data-testid={`leasing-vehicle-termination-cancel-${vehicle.id}`}>
                <Undo2 className="w-3 h-3" />
            </button>
        </span>
    );
};

export const EndLeaseDialog = ({ vehicle, el, onEnded }) => {
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState(today());
    const [saving, setSaving] = useState(false);
    const isToday = date === today();

    const submit = async () => {
        setSaving(true);
        try {
            const res = await api.post(`/leasing/vehicles/${vehicle.id}/terminate`, { end_date: date });
            toast.success(res.data.terminated
                ? (el ? 'Η σύμβαση τερματίστηκε - το όχημα αφαιρέθηκε από τον μισθωτή' : 'Lease ended - vehicle removed from lessee')
                : (el ? 'Ο τερματισμός προγραμματίστηκε' : 'Termination scheduled'));
            setOpen(false);
            onEnded();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button variant="ghost" size="sm" onClick={() => { setDate(today()); setOpen(true); }} className="text-red-500 hover:text-red-600" title={el ? 'Τερματισμός σύμβασης' : 'End lease'} data-testid={`leasing-vehicle-end-${vehicle.id}`}>
                <Ban className="w-4 h-4" />
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-white" data-testid="end-lease-dialog">
                    <DialogHeader>
                        <DialogTitle>{el ? 'Τερματισμός σύμβασης' : 'End lease'} - {vehicle.license_plate}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1">
                            <Label>{el ? 'Ημερομηνία τερματισμού' : 'End date'}</Label>
                            <Input type="date" min={today()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="end-lease-date" />
                        </div>
                        <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-sm">
                            {isToday
                                ? (el ? 'Η σύμβαση τερματίζεται σήμερα: το όχημα αφαιρείται αμέσως από τον στόλο του μισθωτή (το ιστορικό διατηρείται), η χρέωση και οι ειδοποιήσεις σταματούν σήμερα.'
                                    : 'The lease ends today: the vehicle is removed from the lessee fleet now (history kept); billing and alerts stop today.')
                                : (el ? 'Ο μισθωτής ενημερώνεται 2 μήνες νωρίτερα (ή αμέσως αν απομένουν λιγότερο). Την ημέρα τερματισμού το όχημα αφαιρείται αυτόματα και η χρέωση σταματά.'
                                    : 'The lessee is notified 2 months earlier (or now if less time remains). On the end date the vehicle is removed automatically and billing stops.')}
                        </p>
                        <Button onClick={submit} disabled={saving || !date} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="end-lease-confirm">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isToday ? (el ? 'Τερματισμός σήμερα' : 'End today') : (el ? 'Προγραμματισμός τερματισμού' : 'Schedule termination'))}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
